/**
 * Admin API client for CleanYarn Dryclean backend.
 * All monetary values are in Kobo.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.dryclean.thecleanyarn.com';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

/** A bank account a customer can be told to transfer into. */
export interface BankAccount {
  id: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  isDefault: boolean;
}

/** Same, with the fields only the settings screen cares about. */
export interface BankAccountFull extends BankAccount {
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

class AdminApiClient {
  private accessToken: string | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private onSessionRefresh: (() => Promise<boolean>) | null = null;

  setToken(token: string | null) {
    this.accessToken = token;
  }

  getToken(): string | null {
    return this.accessToken;
  }

  /** Register the auth store's refreshSession so the API client can auto-refresh */
  setRefreshHandler(handler: () => Promise<boolean>) {
    this.onSessionRefresh = handler;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    isRetry = false,
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    // Auto-refresh on 401 (expired access token)
    if (response.status === 401 && !isRetry && !path.includes('/auth/') && this.onSessionRefresh) {
      // Deduplicate concurrent refresh attempts
      if (!this.refreshPromise) {
        this.refreshPromise = this.onSessionRefresh().finally(() => { this.refreshPromise = null; });
      }
      const refreshed = await this.refreshPromise;
      if (refreshed) {
        return this.request<T>(method, path, body, true);
      }
    }

    try {
      const data = await response.json();
      return data as ApiResponse<T>;
    } catch {
      return { success: false, error: { code: 'NETWORK', message: `Server error (${response.status})` } } as ApiResponse<T>;
    }
  }

  // ── Auth ──
  async login(input: { identifier: string; password: string }) {
    return this.request<{ accessToken: string; refreshToken: string; expiresIn: number; user: any }>('POST', '/v1/auth/login', input);
  }

  async refreshToken(refreshToken: string) {
    return this.request<{ accessToken: string; refreshToken: string; expiresIn: number }>('POST', '/v1/auth/refresh', { refreshToken });
  }

  async logout() {
    return this.request('POST', '/v1/auth/logout');
  }

  // ── Dashboard ──
  async getDashboardStats(params?: { dateFrom?: string; dateTo?: string }) {
    const query = new URLSearchParams();
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    const qs = query.toString();
    return this.request<any>('GET', `/v1/admin/dashboard/stats${qs ? `?${qs}` : ''}`);
  }

  async getDashboardLive() {
    return this.request<any>('GET', '/v1/admin/dashboard/live');
  }

  async getDashboardManifest() {
    return this.request<any>('GET', '/v1/admin/dashboard/manifest');
  }

  // ── Orders ──
  async getOrders(params?: { page?: number; limit?: number; status?: string; serviceType?: string; search?: string; dateFrom?: string; dateTo?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    if (params?.serviceType) query.set('serviceType', params.serviceType);
    if (params?.search) query.set('search', params.search);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return this.request<{ orders: any[]; page: number; limit: number; total: number }>('GET', `/v1/admin/orders?${query}`);
  }

  async getOrder(id: string) {
    return this.request<any>('GET', `/v1/admin/orders/${id}`);
  }

  async createOrder(input: any) {
    return this.request<any>('POST', '/v1/admin/orders', input);
  }

  async updateOrderStatus(id: string, input: { status: string; note?: string }) {
    return this.request<{ orderId: string; status: string }>('PATCH', `/v1/orders/${id}/status`, input);
  }

  async updateItemStatus(orderId: string, itemId: string, input: { status: string; note?: string }) {
    return this.request<any>('PATCH', `/v1/admin/orders/${orderId}/items/${itemId}/status`, input);
  }

  async bulkUpdateItemStatus(orderId: string, input: { status: string; note?: string }) {
    return this.request<any>('PATCH', `/v1/admin/orders/${orderId}/items/bulk-status`, input);
  }

  async requestDelivery(orderId: string, input?: { deliveryAddress?: string; notes?: string }) {
    return this.request<any>('POST', `/v1/admin/orders/${orderId}/request-delivery`, input ?? {});
  }

  async confirmPayment(orderId: string, input: { paymentMethod: string; reference?: string; bankAccountId?: string }) {
    return this.request<any>('POST', `/v1/pos/orders/${orderId}/confirm-payment`, input);
  }

  // ── Manual bank transfer ──

  async getBankAccounts() {
    return this.request<BankAccount[]>('GET', '/v1/bank-transfer/accounts');
  }

  /**
   * Email goes out from the Worker. WhatsApp does not — it comes back as a wa.me
   * URL for this device to open, because we have no WhatsApp Business API.
   */
  async shareBankDetails(input: {
    orderId: string;
    bankAccountId: string;
    channel: 'email' | 'whatsapp';
    email?: string;
    phone?: string;
  }) {
    return this.request<{ channel: string; whatsappUrl?: string; to?: string; message?: string }>(
      'POST', '/v1/bank-transfer/share', input
    );
  }

  async getAllBankAccounts() {
    return this.request<BankAccountFull[]>('GET', '/v1/bank-transfer/admin/accounts');
  }

  async createBankAccount(input: { bankName: string; accountName: string; accountNumber: string; isDefault?: boolean }) {
    return this.request<{ id: string }>('POST', '/v1/bank-transfer/admin/accounts', input);
  }

  async updateBankAccount(id: string, input: Partial<{ bankName: string; accountName: string; accountNumber: string; isDefault: boolean; isActive: boolean }>) {
    return this.request<{ id: string }>('PATCH', `/v1/bank-transfer/admin/accounts/${id}`, input);
  }

  async deactivateBankAccount(id: string) {
    return this.request<{ id: string }>('DELETE', `/v1/bank-transfer/admin/accounts/${id}`);
  }

  async createPaymentSession(orderId: string, gateway?: string) {
    return this.request<{ sessionToken: string; paymentUrl: string; authorizationUrl?: string; reference?: string; gateway: string }>(
      'POST', '/v1/pos/payment-session', { orderId, gateway }
    );
  }

  async getPaymentStatus(orderId: string) {
    return this.request<{ status: string; paidAt?: string; gateway?: string; gatewayReference?: string }>(
      'GET', `/v1/pos/payment-status/${orderId}`
    );
  }

  // ── Customers ──
  async getCustomers(params?: { page?: number; limit?: number; search?: string; sortBy?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.sortBy) query.set('sortBy', params.sortBy);
    return this.request<{ customers: any[]; page: number; limit: number; total: number }>('GET', `/v1/admin/customers?${query}`);
  }

  async getCustomer(id: string) {
    return this.request<any>('GET', `/v1/admin/customers/${id}`);
  }

  async getCustomerSegments() {
    return this.request<any>('GET', '/v1/admin/customers/segments');
  }

  // ── POS ──
  async posCreateOrder(input: any) {
    return this.request<any>('POST', '/v1/pos/orders', input);
  }

  async posSearchCustomer(query: string) {
    return this.request<any[]>('GET', `/v1/pos/customers/search?q=${encodeURIComponent(query)}`);
  }

  async posCustomerHistory(id: string) {
    return this.request<any>('GET', `/v1/pos/customers/${id}/history`);
  }

  async posGetProducts(priceList?: string) {
    const q = priceList ? `?priceList=${priceList}` : '';
    return this.request<Record<string, any[]>>('GET', `/v1/pos/products${q}`);
  }

  async posTodayOrders() {
    return this.request<{ orders: any[]; count: number; totalRevenueKobo: number }>('GET', '/v1/pos/orders/today');
  }

  async posCashDrawerOpen(openingFloatKobo: number) {
    return this.request<any>('POST', '/v1/pos/cash-drawer/open', { openingFloatKobo });
  }

  async posCashDrawerClose(actualCashKobo: number, notes?: string) {
    return this.request<any>('POST', '/v1/pos/cash-drawer/close', { actualCashKobo, notes });
  }

  async posCashDrawerStatus() {
    return this.request<any>('GET', '/v1/pos/cash-drawer/status');
  }

  async posCashPayout(input: { amountKobo: number; reason: string; approvedBy?: string }) {
    return this.request<any>('POST', '/v1/pos/cash-drawer/payout', input);
  }

  // ── Staff ──
  async getStaff(role?: string) {
    const q = role ? `?role=${role}` : '';
    return this.request<any[]>('GET', `/v1/admin/staff${q}`);
  }

  async createStaff(input: { firstName: string; lastName: string; phone: string; email?: string; password: string; role: string; licenseNumber?: string; vehicleId?: string; pin?: string }) {
    return this.request<any>('POST', '/v1/admin/staff', input);
  }

  async updateStaff(id: string, input: any) {
    return this.request<any>('PATCH', `/v1/admin/staff/${id}`, input);
  }

  async deleteStaff(id: string) {
    return this.request<any>('DELETE', `/v1/admin/staff/${id}`);
  }

  // ── Drivers ──
  async getDrivers() {
    return this.request<any[]>('GET', '/v1/admin/drivers');
  }

  async getDriverLocations() {
    return this.request<any[]>('GET', '/v1/admin/drivers/locations');
  }

  async getDriver(id: string) {
    return this.request<any>('GET', `/v1/admin/drivers/${id}`);
  }

  // ── Products ──
  async getProducts() {
    return this.request<any[]>('GET', '/v1/admin/products');
  }

  async createProduct(input: any) {
    return this.request<any>('POST', '/v1/admin/products', input);
  }

  async updateProduct(id: string, input: any) {
    return this.request<any>('PATCH', `/v1/admin/products/${id}`, input);
  }

  async deactivateImportedProducts() {
    return this.request<{ deactivated: number; products: { id: string; name: string }[] }>(
      'POST', '/v1/admin/products/deactivate-imported'
    );
  }

  async getOrderTags(orderId: string) {
    return this.request<{ orderNumber: string; totalItems: number; tags: any[] }>('GET', `/v1/admin/orders/${orderId}/tags`);
  }

  // ── Release ──
  async getReleaseRequests(status?: string) {
    const query = status ? `?status=${status}` : '';
    return this.request<any[]>('GET', `/v1/release/requests${query}`);
  }

  async getReleaseRequest(id: string) {
    return this.request<any>('GET', `/v1/release/requests/${id}`);
  }

  async createReleaseRequest(orderId: string, notes?: string) {
    return this.request<{ id: string; paymentVerified: boolean }>('POST', '/v1/release/requests', { orderId, notes });
  }

  async scanReleaseBarcode(requestId: string, barcode: string) {
    return this.request<{ item: any; paymentStatus: string; paymentVerified: boolean }>(
      'POST', `/v1/release/requests/${requestId}/scan`, { barcode }
    );
  }

  async confirmRelease(requestId: string) {
    return this.request<any>('POST', `/v1/release/requests/${requestId}/release`);
  }

  async rejectRelease(requestId: string, reason: string) {
    return this.request<any>('POST', `/v1/release/requests/${requestId}/reject`, { reason });
  }

  // ── Capacity ──
  async getCapacitySettings() {
    return this.request<any>('GET', '/v1/admin/capacity/settings');
  }

  async updateCapacitySettings(input: { trackingMode?: string; warningThresholdPercent?: number }) {
    return this.request<any>('PUT', '/v1/admin/capacity/settings', input);
  }

  async getCapacityConfig() {
    return this.request<any[]>('GET', '/v1/admin/capacity/config');
  }

  async upsertCapacityConfig(input: { targetId: string; targetType: string; dailyLimit: number }) {
    return this.request<any>('PUT', '/v1/admin/capacity/config', input);
  }

  async deleteCapacityConfig(id: string) {
    return this.request<any>('DELETE', `/v1/admin/capacity/config/${id}`);
  }

  async getCapacityUtilization(date?: string) {
    const q = date ? `?date=${date}` : '';
    return this.request<any>('GET', `/v1/admin/capacity/utilization${q}`);
  }

  async checkCapacity(date: string) {
    return this.request<{ date: string; withinCapacity: boolean; warnings: any[] }>('GET', `/v1/admin/capacity/check?date=${date}`);
  }

  // ── Promotions ──
  async getPromotions() {
    return this.request<any[]>('GET', '/v1/admin/promotions');
  }

  async createPromotion(input: any) {
    return this.request<any>('POST', '/v1/admin/promotions', input);
  }

  async updatePromotion(id: string, input: any) {
    return this.request<any>('PATCH', `/v1/admin/promotions/${id}`, input);
  }

  // ── Audit Logs ──
  async getAuditLogs(params?: { page?: number; limit?: number; actorId?: string; action?: string; resource?: string; dateFrom?: string; dateTo?: string; search?: string }) {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.actorId) query.set('actorId', params.actorId);
    if (params?.action) query.set('action', params.action);
    if (params?.resource) query.set('resource', params.resource);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    if (params?.search) query.set('search', params.search);
    return this.request<{ logs: any[]; page: number; limit: number; total: number }>('GET', `/v1/admin/audit-logs?${query}`);
  }

  // ── Reports ──
  async getFinancialReport(params?: { dateFrom?: string; dateTo?: string }) {
    const query = new URLSearchParams();
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return this.request<any>('GET', `/v1/admin/reports/financial?${query}`);
  }

  async getOperationsReport(params?: { dateFrom?: string; dateTo?: string }) {
    const query = new URLSearchParams();
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return this.request<any>('GET', `/v1/admin/reports/operations?${query}`);
  }

  async getCustomerReport(params?: { dateFrom?: string; dateTo?: string }) {
    const query = new URLSearchParams();
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return this.request<any>('GET', `/v1/admin/reports/customers?${query}`);
  }

  async getOutstandingReport(params?: { dateFrom?: string; dateTo?: string }) {
    const query = new URLSearchParams();
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return this.request<any>('GET', `/v1/admin/reports/outstanding?${query}`);
  }

  async getLostItemsReport() {
    return this.request<any>('GET', '/v1/admin/lost-items/report');
  }

  // ── Finora sync ──
  async getFinoraSyncStatus(status?: string) {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    query.set('limit', '100');
    return this.request<any[]>('GET', `/v1/admin/finora/sync-status?${query}`);
  }

  async retryFinoraSync(syncId: string) {
    return this.request<any>('POST', `/v1/admin/finora/retry/${syncId}`);
  }

  async testFinoraConnection() {
    return this.request<any>('POST', '/v1/admin/finora/test');
  }

  async getFinoraInvoiceStatus(status?: string) {
    const query = new URLSearchParams();
    if (status) query.set('status', status);
    query.set('limit', '100');
    return this.request<any[]>('GET', `/v1/admin/finora/invoice-status?${query}`);
  }

  async retryFinoraInvoice(orderId: string) {
    return this.request<any>('POST', `/v1/admin/finora/invoice-retry/${orderId}`);
  }

  async getFuelReport(params?: { dateFrom?: string }) {
    const query = new URLSearchParams();
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    return this.request<any>('GET', `/v1/admin/fuel/report?${query}`);
  }

  /**
   * Fetch a server-generated CSV from an authenticated endpoint (sends the
   * Bearer header, refreshes once on 401) and trigger a browser download.
   * Use this for endpoints that export the full filtered set server-side;
   * a plain <a href> can't attach the auth header.
   */
  async downloadAuthedCsv(path: string, filename: string): Promise<void> {
    const doFetch = () => {
      const headers: Record<string, string> = {};
      if (this.accessToken) headers['Authorization'] = `Bearer ${this.accessToken}`;
      return fetch(`${API_BASE}${path}`, { headers });
    };

    let response = await doFetch();
    if (response.status === 401 && this.onSessionRefresh) {
      if (!this.refreshPromise) {
        this.refreshPromise = this.onSessionRefresh().finally(() => { this.refreshPromise = null; });
      }
      if (await this.refreshPromise) response = await doFetch();
    }

    if (!response.ok) throw new Error('Export failed');

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ── Inventory ──
  async getInventory(lowStockOnly?: boolean) {
    const q = lowStockOnly ? '?lowStock=true' : '';
    return this.request<any[]>('GET', `/v1/admin/inventory${q}`);
  }

  async updateInventory(id: string, input: any) {
    return this.request<any>('PATCH', `/v1/admin/inventory/${id}`, input);
  }

  // ── Import ──
  async importPreview(rows: string[][]) {
    return this.request<any>('POST', '/v1/admin/import/preview', { rows });
  }

  async importCommit(rows: string[][], dryRun?: boolean) {
    return this.request<any>('POST', '/v1/admin/import/commit', { rows, dryRun });
  }

  // ── Staff Password Reset ──
  async resetStaffPassword(staffId: string, newPassword: string) {
    return this.request<{ message: string }>('POST', `/v1/admin/staff/${staffId}/reset-password`, { newPassword });
  }
}

export const api = new AdminApiClient();
